// @flow
import React, { Children, type Node } from 'react';
import { isString } from 'lodash';

import { ICONS } from 'Components/utils/icon/icons';
import { getFormattedLabel } from 'Components/utils/form/form.util';

import { Icon, type IconName } from 'Components/frameworks/icon/Icon.jsx';
import { Spinner } from 'Components/frameworks/spinner/Spinner.jsx';

import { themes } from './FormControl.themes';

type Props = {
  children: Node,
  className?: string,
  dir?: ?string,
  hasClear?: boolean,
  iconLeft?: IconName,
  iconRight?: IconName,
  id?: string,
  isActive?: boolean,
  isDisabled?: boolean,
  isLoading?: boolean,
  isReadOnly?: boolean,
  isRequired?: boolean,
  label?: string | Node,
  notes?: string,
  onClear?: (SyntheticEvent<>) => void,
  size?: SizeProps | 'none',
  theme?: string,
  validationState?: string,
};

export function FormControl(props: Props) {
  const {
    children,
    className,
    dir,
    hasClear,
    iconLeft,
    iconRight,
    id,
    isDisabled,
    isLoading,
    isReadOnly,
    isRequired,
    label,
    notes,
    onClear,
    theme = 'default',
  } = props;

  if (!Children.count(children)) return null;

  const {
    thClearButton,
    thClearIconSize,
    thField,
    thFieldStyle,
    thIconLeft,
    thIconRight,
    thInput,
    thLabel,
    thLoader,
    thNotes,
    thSpinnerColor,
  } = themes[theme](props);

  return (
    <div className={className} dir={dir}>
      {!!label && (
        <label className={thLabel} htmlFor={id}>
          {isString(label) ? getFormattedLabel(label, isRequired) : label}
        </label>
      )}
      <div className={thField} style={thFieldStyle}>
        {!!iconLeft && (
          <div className={thIconLeft}>
            <Icon name={iconLeft} width="0.875rem" />
          </div>
        )}
        <div className={thInput}>
          {children}
          {isLoading &&
            !isDisabled &&
            !isReadOnly && (
              <div className={thLoader}>
                <Spinner color={thSpinnerColor} size="small" />
              </div>
            )}
          {hasClear &&
            !isDisabled &&
            !isLoading &&
            !isReadOnly && (
              <button
                className={thClearButton}
                data-testid="FormControlClear"
                onClick={onClear}
                type="button"
              >
                <Icon name={ICONS.CROSS} width={thClearIconSize} />
              </button>
            )}
        </div>
        {!!iconRight && (
          <div className={thIconRight}>
            <Icon name={iconRight} />
          </div>
        )}
      </div>
      {!!notes && (
        <div className={thNotes} data-testid="FormControlNotes">
          {notes}
        </div>
      )}
    </div>
  );
}
